import React, { useState, useMemo } from 'react';
import { 
  Row, Col, Card, Table, Tag, Button, Space, Typography, 
  Input, Tooltip, Progress, Alert, Popover, Empty
} from 'antd';
import { 
  InfoCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const TrackedStopPerformanceTab = ({ 
  stopData, 
  onStopClick,
  tripStartTimes
}) => {
  const [stopSearchText, setStopSearchText] = useState('');
  const [selectedStopDetail, setSelectedStopDetail] = useState(null);
  const [stopTripData, setStopTripData] = useState([]);

  // Helper to check if a stop is tracked
  const isStopTracked = (stop) => {
    const hasActualDeparture = Number.isFinite(stop.ACTUAL_DEPARTURE_TIME);
    const hasActualArrival = Number.isFinite(stop.ACTUAL_ARRIVAL_TIME);
    return hasActualDeparture || hasActualArrival;
  };

  const handleStopClick = (tsn) => {
    const selectedStop = stopStats.find(stop => stop.tsn === tsn);
    setSelectedStopDetail(selectedStop);
    
    // Filter trip data for trips that pass through this stop
    const tripsWithStop = stopData.filter(record => record.TSN === tsn);
    
    // Group by trip and calculate tracking status for each trip at this stop
    const tripGroups = {};
    tripsWithStop.forEach(record => {
      const tripKey = `${record.TRIP_ID}_${record.DATE_ID}`;
      if (!tripGroups[tripKey]) {
        tripGroups[tripKey] = {
          tripId: record.TRIP_ID,
          route: record.ROUTE,
          date: record.DATE_ID,
          dateFormatted: record.DATE_FORMATTED || record.DATE_ID,
          driverId: record.DRIVER_ID,
          depot: record.DEPOT,
          vehicleShift: record.VEHICLE_SHIFT,
          stopType: record.TSN_FIRST_LAST_IND === 'F' ? 'First' : 
                   record.TSN_FIRST_LAST_IND === 'L' ? 'Last' : 'Mid',
          scheduledArrival: record.SCHEDULED_ARRIVAL_TIME,
          scheduledDeparture: record.SCHEDULED_DEPARTURE_TIME,
          actualArrival: record.ACTUAL_ARRIVAL_TIME,
          actualDeparture: record.ACTUAL_DEPARTURE_TIME,
          isTracked: isStopTracked(record),
          timeRecordSequence: record.TIME_RECORD_SEQUENCE
        };
      }
    });
    
    setStopTripData(Object.values(tripGroups));
  };

  const renderStopTripView = () => {
    if (!selectedStopDetail || stopTripData.length === 0) {
      return null;
    }

    const tripColumns = [
      {
        title: 'Date',
        dataIndex: 'dateFormatted',
        key: 'date',
        sorter: (a, b) => String(a.date).localeCompare(String(b.date)),
        width: 100,
      },
      {
        title: 'Trip Start',
        dataIndex: 'tripId',
        key: 'tripId',
        sorter: (a, b) => {
                  const timeA = tripStartTimes && tripStartTimes[a.tripKey]?.time || '';
        const timeB = tripStartTimes && tripStartTimes[b.tripKey]?.time || '';
          return timeA.localeCompare(timeB);
        },
        render: (tripId) => tripStartTimes && tripStartTimes[tripId]?.time || tripId,
        width: 100,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        sorter: (a, b) => String(a.route || '').localeCompare(String(b.route || '')),
        width: 80,
      },
      {
        title: 'Stop Type',
        dataIndex: 'stopType',
        key: 'stopType',
        render: stopType => {
          let color = 'default';
          if (stopType === 'First') color = 'green';
          if (stopType === 'Mid') color = 'blue';
          if (stopType === 'Last') color = 'orange';
          
          return <Tag color={color}>{stopType}</Tag>;
        },
        filters: [
          { text: 'First', value: 'First' },
          { text: 'Mid', value: 'Mid' },
          { text: 'Last', value: 'Last' }
        ],
        onFilter: (value, record) => record.stopType === value,
        width: 90,
      },
      {
        title: 'Tracking Status',
        dataIndex: 'isTracked',
        key: 'isTracked',
        render: (isTracked, record) => {
          const hasArrival = Number.isFinite(record.actualArrival);
          const hasDeparture = Number.isFinite(record.actualDeparture);
          
          if (hasArrival || hasDeparture) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#52c41a' 
                }} />
                <EyeOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                <Text style={{ color: '#262626', fontSize: '13px' }}>Tracked</Text>
              </div>
            );
          } else {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#d9d9d9' 
                }} />
                <EyeInvisibleOutlined style={{ color: '#d9d9d9', fontSize: '14px' }} />
                <Text style={{ color: '#8c8c8c', fontSize: '13px' }}>-</Text>
              </div>
            );
          }
        },
        filters: [
          { text: 'Tracked', value: 'tracked' },
          { text: 'Not Tracked', value: 'none' }
        ],
        onFilter: (value, record) => {
          const hasArrival = Number.isFinite(record.actualArrival);
          const hasDeparture = Number.isFinite(record.actualDeparture);
          
          if (value === 'tracked') return hasArrival || hasDeparture;
          if (value === 'none') return !hasArrival && !hasDeparture;
          return false;
        },
        width: 130,
      },
      {
        title: 'Data Availability',
        key: 'dataAvailability',
        render: (_, record) => {
          const hasArrival = Number.isFinite(record.actualArrival);
          const hasDeparture = Number.isFinite(record.actualDeparture);
          
          const tags = [];
          if (!hasArrival) {
            tags.push(
              <Tag key="no-arrival" style={{ 
                fontSize: '11px', 
                padding: '2px 6px',
                backgroundColor: '#f5f5f5',
                color: '#8c8c8c',
                border: '1px solid #d9d9d9'
              }}>
                No Arrival
              </Tag>
            );
          }
          if (hasDeparture) {
            tags.push(
              <Tag key="departure" color="green" style={{ fontSize: '11px', padding: '2px 6px' }}>
                Departure ✓
              </Tag>
            );
          }
          if (!hasDeparture) {
            tags.push(
              <Tag key="no-departure" style={{ 
                fontSize: '11px', 
                padding: '2px 6px',
                backgroundColor: '#f5f5f5',
                color: '#8c8c8c',
                border: '1px solid #d9d9d9'
              }}>
                No Departure
              </Tag>
            );
          }
          
          return <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{tags}</div>;
        },
        width: 150,
      },
      {
        title: 'Actual Arrival',
        dataIndex: 'actualArrival',
        key: 'actualArrival',
        render: (time) => (
          <Text style={{ 
            fontFamily: 'monospace', 
            fontSize: '13px',
            color: time ? '#262626' : '#bfbfbf'
          }}>
            {time ? new Date(time * 1000).toLocaleTimeString() : '-'}
          </Text>
        ),
        width: 120,
      },
      {
        title: 'Actual Departure',
        dataIndex: 'actualDeparture',
        key: 'actualDeparture',
        render: (time) => (
          <Text style={{ 
            fontFamily: 'monospace', 
            fontSize: '13px',
            color: time ? '#262626' : '#bfbfbf'
          }}>
            {time ? new Date(time * 1000).toLocaleTimeString() : '-'}
          </Text>
        ),
        width: 120,
      }
    ];

    return (
      <Card
        title={
          <Space>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                setSelectedStopDetail(null);
                setStopTripData([]);
              }}
              style={{ padding: '4px 8px', fontSize: '14px' }}
            >
              Back to Stops
            </Button>
            <Text strong style={{ fontSize: '16px' }}>
              {`Trips for Stop ${selectedStopDetail.tsn} ${selectedStopDetail.stopName ? `(${selectedStopDetail.stopName})` : ''}`}
            </Text>
          </Space>
        }
        style={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px'
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Alert
            message={`Showing ${stopTripData.length} trip instances that pass through this stop`}
            description={
              <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                <div>
                  <Text strong>Tracking Coverage: </Text>
                  <Text style={{ color: selectedStopDetail.trackingCoverage >= 80 ? '#52c41a' : selectedStopDetail.trackingCoverage >= 50 ? '#fa8c16' : '#f5222d' }}>
                    {Math.round(selectedStopDetail.trackingCoverage)}%
                  </Text>
                </div>
                <div>
                  <Text strong>Tracked Records: </Text>
                  <Text>{selectedStopDetail.trackedRecords} / {selectedStopDetail.totalRecords}</Text>
                </div>
              </div>
            }
            type="info"
            showIcon
            style={{ borderRadius: '6px' }}
          />
        </div>
        
        <Table
          columns={tripColumns}
          dataSource={stopTripData}
          rowKey={record => `${record.tripId}-${record.date}`}
          pagination={{ pageSize: 20 }}
          size="small"
          bordered
          style={{ 
            backgroundColor: '#fff',
            borderRadius: '6px'
          }}
        />
      </Card>
    );
  };

  // Calculate tracking statistics for each stop
  const stopStats = useMemo(() => {
    if (!stopData || stopData.length === 0) {
      return [];
    }
    const stats = {};
    
    stopData.forEach(stop => {
      const tsn = stop.TSN;
      if (!stats[tsn]) {
        stats[tsn] = {
          tsn,
          stopName: stop.STOP_NAME || `Stop ${tsn}`,
          totalRecords: 0,
          trackedRecords: 0,
          untrackedRecords: 0,
          stopTypes: new Set(),
          routes: new Set(),
          dates: new Set()
        };
      }
      
      const stopStat = stats[tsn];
      stopStat.totalRecords++;
      stopStat.routes.add(stop.ROUTE);
      stopStat.dates.add(stop.DATE_ID);
      
      // Track stop types
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        stopStat.stopTypes.add('First');
      } else if (stop.TSN_FIRST_LAST_IND === 'L') {
        stopStat.stopTypes.add('Last');
      } else {
        stopStat.stopTypes.add('Mid');
      }
      
      // Check if this record is tracked
      if (isStopTracked(stop)) {
        stopStat.trackedRecords++;
      } else {
        stopStat.untrackedRecords++;
      }
    });
    
    // Calculate percentages and convert sets to arrays
    return Object.values(stats).map(stop => ({
      ...stop,
      trackingCoverage: (stop.trackedRecords / stop.totalRecords) * 100,
      untrackedPercent: (stop.untrackedRecords / stop.totalRecords) * 100,
      stopTypes: Array.from(stop.stopTypes),
      routes: Array.from(stop.routes),
      routesString: Array.from(stop.routes).join(', '),
      dateCount: stop.dates.size
    }));
  }, [stopData]);

  // Early return if no data
  if (stopStats.length === 0) {
    return (
      <Empty 
        description="No stop data available" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  }

  // Identify stops with poor tracking coverage
  const poorlyTrackedStops = stopStats.filter(stop => stop.trackingCoverage < 70);
  
  // Filter stops based on search text
  const filteredStopStats = stopSearchText 
    ? stopStats.filter(stop => 
        String(stop.tsn).toLowerCase().includes(stopSearchText.toLowerCase()) || 
        (stop.stopName && stop.stopName.toLowerCase().includes(stopSearchText.toLowerCase()))
      )
    : stopStats;

  // Define columns for the stops table
  const stopColumns = [
    {
      title: 'Stop ID',
      dataIndex: 'tsn',
      key: 'tsn',
      sorter: (a, b) => String(a.tsn).localeCompare(String(b.tsn)),
      render: tsn => (
        <Button
            type="link"
            style={{
              padding: 0,
              fontSize: '14px',
              fontWeight: 'bold',
              textDecoration: 'underline'
            }}
            onClick={() => handleStopClick(tsn)}>{tsn}</Button>
      ),
      width: 80,
    },
    {
      title: 'Stop Name',
      dataIndex: 'stopName',
      key: 'stopName',
      sorter: (a, b) => String(a.stopName).localeCompare(String(b.stopName)),
      render: (stopName, record) => (
        <Button
            type="link"
            style={{
              padding: 0,
              fontSize: '14px',
              fontWeight: 'bold',
              textDecoration: 'underline'
            }}
            onClick={() => handleStopClick(record.tsn)}>{stopName}</Button>
      ),
      width: 100,
    },
    {
      title: 'Stop Type',
      key: 'stopType',
      render: (_, record) => {
        const stopTypes = record.stopTypes || [];
        return (
          <div>
            {stopTypes.map(type => {
              let color = 'default';
              if (type === 'First') color = 'green';
              if (type === 'Mid') color = 'blue';
              if (type === 'Last') color = 'orange';
              
              return <Tag color={color} key={type}>{type}</Tag>;
            })}
          </div>
        );
      },
      filters: [
        { text: 'First', value: 'First' },
        { text: 'Mid', value: 'Mid' },
        { text: 'Last', value: 'Last' }
      ],
      onFilter: (value, record) => (record.stopTypes || []).includes(value),
      width: 80,
    },
    {
      title: 'Tracking Coverage %',
      dataIndex: 'trackingCoverage',
      key: 'trackingCoverage',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 50) color = '#f5222d';
        else if (value < 80) color = '#fa8c16';
        
        return (
          <Tooltip title={`${Math.round(value)}% tracked\n${record.trackedRecords} tracked records\n${record.untrackedRecords} untracked records`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => a.trackingCoverage - b.trackingCoverage,
      width: 120,
    },
    {
      title: 'Total Records',
      dataIndex: 'totalRecords',
      key: 'totalRecords',
      sorter: (a, b) => a.totalRecords - b.totalRecords,
      width: 100,
    },
    {
      title: 'Tracked',
      dataIndex: 'trackedRecords',
      key: 'trackedRecords',
      render: (val) => (
        <Tag color="green" icon={<EyeOutlined />}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => a.trackedRecords - b.trackedRecords,
      width: 80,
    },
    {
      title: 'Untracked',
      dataIndex: 'untrackedRecords',
      key: 'untrackedRecords',
      render: (val) => (
        <Tag color="red" icon={<EyeInvisibleOutlined />}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => a.untrackedRecords - b.untrackedRecords,
      width: 90,
    },
    {
      title: 'Routes',
      dataIndex: 'routesString',
      key: 'routes',
      render: (routesString, record) => (
        <Tooltip title={`Stop serves ${record.routes.length} route(s): ${routesString}`}>
          <Tag color="blue">{record.routes.length} route{record.routes.length !== 1 ? 's' : ''}</Tag>
        </Tooltip>
      ),
      sorter: (a, b) => a.routes.length - b.routes.length,
      width: 80,
    },
    {
      title: 'Days',
      dataIndex: 'dateCount',
      key: 'dateCount',
      render: (dateCount) => (
        <Tag color="purple">{dateCount} day{dateCount !== 1 ? 's' : ''}</Tag>
      ),
      sorter: (a, b) => a.dateCount - b.dateCount,
      width: 70,
    }
  ];

  // Show trip detail view if a stop is selected
  if (selectedStopDetail) {
    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            {renderStopTripView()}
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <span>Stop Level Tracking Performance</span>
                <Tooltip title="This table shows tracking coverage metrics for each stop in the network. Tracking coverage indicates how often GPS or timing data is successfully captured for each stop.">
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
          >
            {poorlyTrackedStops.length > 0 && (
              <Alert
                message={`${poorlyTrackedStops.length} stops have tracking coverage below 70%`}
                description="Poor tracking coverage may indicate GPS signal issues, device problems, or stops in areas with limited connectivity. Consider investigating these stops for hardware or positioning issues."
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}
            
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
              <Input.Search
                placeholder="Search by Stop ID (TSN) or Stop Name"
                onChange={e => setStopSearchText(e.target.value)}
                onSearch={value => setStopSearchText(value)}
                value={stopSearchText}
                allowClear
                enterButton
                style={{ width: 400 }}
              />
              {stopSearchText && (
                <>
                  <Text style={{ margin: '0 16px' }}>
                    Found {filteredStopStats.length} {filteredStopStats.length === 1 ? 'stop' : 'stops'} matching "{stopSearchText}"
                  </Text>
                  <Button 
                    type="link" 
                    onClick={() => setStopSearchText('')}
                    style={{ padding: '0 8px' }}
                  >
                    Reset
                  </Button>
                </>
              )}
            </div>
            
            <Table
              dataSource={filteredStopStats}
              columns={stopColumns}
              rowKey="tsn"
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TrackedStopPerformanceTab; 